import { type AxialCoord, hexDistance, hexEquals, hexKey, hexNeighbors } from "../hex";
import type { FactionId, HexOwner, HexState, RunState, Unit } from "../types/state";
import { allied } from "./sides";

/**
 * The board every system shares: a hex or a unit by coord or id (cached indexes keyed by array
 * identity), a faction's land and its predicates, the enemy near a hex, and the one-hex writes
 * (`patchHexAt`, `replaceHex`). The nearest of anything is `nearestTo` (../pick.ts); one-unit
 * writes are in ../units/unit.ts.
 */

/** A map's shape: the position of every hex by key, and each hex's neighbours as positions in insertion order. */
interface HexTopology {
  readonly index: Map<string, number>;
  readonly neighbours: readonly (readonly number[])[];
}

type Placed = { readonly coord: AxialCoord };

/**
 * The topology of a `hexes` array, cached (ADR-0012): a pure function of the coords, so the
 * cache holds nothing a run depends on. On 600 hexes scanning per lookup and sorting
 * neighbours per step were most of a tick. A run makes a new array whenever a hex changes but
 * keeps every coord object, so a second cache by the first coord object hands the same
 * topology on when every coord is the same object in the same place: a King's road is searched
 * over a kingdom's own land only, and two such subsets can share their ends and their length.
 */
const TOPOLOGY_BY_ARRAY = new WeakMap<readonly Placed[], HexTopology>();

const TOPOLOGY_BY_FIRST_COORD = new WeakMap<
  AxialCoord,
  { readonly coords: readonly AxialCoord[]; readonly topology: HexTopology }
>();

export function hexTopology(hexes: readonly Placed[]): HexTopology {
  const cached = TOPOLOGY_BY_ARRAY.get(hexes);
  if (cached) {
    return cached;
  }
  const first = hexes[0]?.coord;
  const inherited = first ? TOPOLOGY_BY_FIRST_COORD.get(first) : undefined;
  if (
    inherited &&
    inherited.coords.length === hexes.length &&
    inherited.coords.every((coord, position) => coord === hexes[position]?.coord)
  ) {
    TOPOLOGY_BY_ARRAY.set(hexes, inherited.topology);
    return inherited.topology;
  }
  const index = new Map<string, number>();
  for (const [position, hex] of hexes.entries()) {
    // The first of two equal coords wins, as `find` did.
    if (!index.has(hexKey(hex.coord))) {
      index.set(hexKey(hex.coord), position);
    }
  }
  const topology: HexTopology = {
    index,
    neighbours: hexes.map((hex) => neighbourPositions(index, hex.coord)),
  };
  TOPOLOGY_BY_ARRAY.set(hexes, topology);
  if (first) {
    TOPOLOGY_BY_FIRST_COORD.set(first, { coords: hexes.map((hex) => hex.coord), topology });
  }
  return topology;
}

/** The on-map neighbours of a coord as positions, lowest first: the order every road search expands in. */
export function neighbourPositions(
  index: ReadonlyMap<string, number>,
  coord: AxialCoord,
): number[] {
  const positions: number[] = [];
  for (const neighbour of hexNeighbors(coord)) {
    const position = index.get(hexKey(neighbour));
    if (position !== undefined) {
      positions.push(position);
    }
  }
  return positions.sort((a, b) => a - b);
}

export function hexIndex(hexes: readonly Placed[]): Map<string, number> {
  return hexTopology(hexes).index;
}

/** A coord's place on the map (0 off it): the last tie-break of the scripts' rankings. */
export function mapOrder(hexes: readonly Placed[]): (coord: AxialCoord) => number {
  const index = hexIndex(hexes);
  return (coord) => index.get(hexKey(coord)) ?? 0;
}

/**
 * The hex at `coord` in a hexes array, a run's or a step's draft of it (a draft keeps every coord
 * object, so it shares the run's cached index); undefined off the map.
 */
export function hexAt<Hex extends Placed>(
  hexes: readonly Hex[],
  coord: AxialCoord,
): Hex | undefined {
  const position = hexIndex(hexes).get(hexKey(coord));
  return position === undefined ? undefined : hexes[position];
}

export function findHex(state: RunState, coord: AxialCoord): HexState | undefined {
  return hexAt(state.hexes, coord);
}

export function ownedBy(state: RunState, faction: FactionId): HexState[] {
  return state.hexes.filter((hex) => hex.owner === faction);
}

/** How many hexes a faction holds: the score every verdict compares. */
export function landCount(state: RunState, faction: FactionId): number {
  return ownedBy(state, faction).length;
}

/** Player territory; kept as the common case for the HUD and tests. */
export function claimedHexes(state: RunState): HexState[] {
  return ownedBy(state, "player");
}

/** The faction's own, unrazed hex: ground it stands on, walks and builds on. */
export function isOwnGround<Hex extends HexState>(
  hex: Hex | undefined,
  faction: FactionId,
): hex is Hex {
  return hex !== undefined && hex.owner === faction && !hex.razed;
}

/** Own ground with nothing built on it: where a city or an improvement may go. */
export function isPlainOwnHex<Hex extends HexState>(
  hex: Hex | undefined,
  faction: FactionId,
): hex is Hex {
  return isOwnGround(hex, faction) && hex.structure === "none";
}

/** A faction's own, unrazed land, in map order: the only ground its King walks. */
export function ownGround(state: RunState, faction: FactionId): HexState[] {
  return state.hexes.filter((hex) => isOwnGround(hex, faction));
}

/**
 * Whether a unit of no ally of `owner` (K6) stands within `range` of `coord` (for a neutral hex any unit):
 * the enemy beside a hex that stops its walls regrowing, a unit healing, a soldier reading.
 */
export function enemyWithin(
  state: RunState,
  owner: HexOwner,
  coord: AxialCoord,
  range: number,
): boolean {
  return state.units.some(
    (other) => !allied(state.rules, other.owner, owner) && hexDistance(other.coord, coord) <= range,
  );
}

/**
 * The run with the hex at `coord` patched. The patch's keys follow the hex's own, as an inline
 * `{ ...hex, key: value }` does, so the bytes are the same.
 */
export function patchHexAt(
  state: RunState,
  coord: AxialCoord,
  patch: Partial<Omit<HexState, "coord">>,
): RunState {
  return {
    ...state,
    hexes: state.hexes.map((hex) => (hexEquals(hex.coord, coord) ? { ...hex, ...patch } : hex)),
  };
}

/** The run with the hex `before` (by identity) replaced by `after`. */
export function replaceHex(state: RunState, before: HexState, after: HexState): RunState {
  return { ...state, hexes: state.hexes.map((hex) => (hex === before ? after : hex)) };
}

/** Units by hex and by id, cached per `units` array the way `hexIndex` is: the script asks "who stands there" for every hex it ranks. */
const UNIT_INDEXES = new WeakMap<
  readonly Unit[],
  { readonly byHex: Map<string, Unit>; readonly byId: Map<number, Unit> }
>();

function unitIndex(units: readonly Unit[]): {
  readonly byHex: Map<string, Unit>;
  readonly byId: Map<number, Unit>;
} {
  let index = UNIT_INDEXES.get(units);
  if (!index) {
    index = { byHex: new Map(), byId: new Map() };
    for (const unit of units) {
      // The first in creation order wins, as `find` did.
      if (!index.byHex.has(hexKey(unit.coord))) {
        index.byHex.set(hexKey(unit.coord), unit);
      }
      if (!index.byId.has(unit.id)) {
        index.byId.set(unit.id, unit);
      }
    }
    UNIT_INDEXES.set(units, index);
  }
  return index;
}

export function unitAt(state: RunState, coord: AxialCoord): Unit | undefined {
  return unitIndex(state.units).byHex.get(hexKey(coord));
}

export function unitById(state: RunState, id: number): Unit | undefined {
  return unitIndex(state.units).byId.get(id);
}

export function unitsOf(state: RunState, faction: FactionId): Unit[] {
  return state.units.filter((unit) => unit.owner === faction);
}
